/**
 * Demand Explorer data layer (task 14.3, R5, R7.2, R7.5, R7.6, R7.7).
 *
 * Wired to `POST /api/manpower/demand`, which calculates Demand_Hours for every
 * Field_Force_Demand_Driver matching the filter and returns the driver rows, the
 * per-Initiative totals and the reconciliation outcome (R5.1-R5.9).
 *
 * ## Ids, not names
 *
 * The wire carries `activityTypeId`, `populationUnitId` and `regionId` rather
 * than display names, because those are master data the client already holds
 * (`useMasterDataSet`) and repeating them per driver row would put the same name
 * on the wire hundreds of times. The view model joins the names in; this layer
 * stays a faithful mirror of the response.
 *
 * ## `demandHours: null` is Data_Incomplete
 *
 * R5.9: a driver with no resolvable Effort_Assumption has no Demand_Hours, and
 * the wire says so with `null` plus an entry in `missingEffortAssumptions`. The
 * view model counts those rather than treating them as zero.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '@/shared/api/client';
import { manpowerQueryKeys } from '../manpower-query-keys';

/** Request parameters for the demand calculation endpoint (R7.2, UI spec §5.2). */
export interface DemandExplorerRequest {
  readonly planningYear: number;
  readonly granularity: 'month' | 'calendar-week';
  readonly ffRole?: string;
  readonly regionId?: string;
}

/** One period's share of a driver's Demand_Hours (R5.6's period allocation). */
export interface PeriodAllocation {
  readonly periodId: string;
  readonly hours: number;
}

/** One Platform's share of a driver's Demand_Hours (R5.3's Platform allocation). */
export interface PlatformAllocation {
  readonly platformId: string;
  readonly hours: number;
}

/**
 * The three Reconciliation_Control clauses of R5.7, each reported separately.
 *
 * Separately rather than as one boolean because they fail for different reasons and
 * name different defects: a driver's periods not summing to its own total is an
 * allocation fault, an Initiative's drivers not summing to its total is a rollup
 * fault, and the role totals not summing to the grand total is a grouping fault.
 * Reporting only the conjunction told a reader something was wrong but not what.
 */
export interface ReconciliationClauses {
  readonly driverPeriodsHold: boolean;
  readonly initiativeDriversHold: boolean;
  readonly roleTotalsHold: boolean;
  readonly holds: boolean;
}

/** One calculated Field_Force_Demand_Driver row (R5.1-R5.9, R7.2). */
export interface DemandDriver {
  readonly driverId: string;
  readonly initiativeId: string;
  readonly initiativeRef: string;
  readonly title: string;
  readonly ffRole: string;
  readonly activityTypeId: string;
  readonly populationUnitId: string;
  readonly regionId: string | null;
  readonly effortAssumptionId: string | null;
  readonly effortAssumptionVersion: number | null;
  readonly calculationDate: string;
  /** `null` for a Data_Incomplete driver (R5.9). */
  readonly demandHours: number | null;
  readonly platformAllocations: readonly PlatformAllocation[];
  readonly periodAllocations: readonly PeriodAllocation[];
}

/** One Initiative's rolled-up Demand_Hours (R5.7's reconciliation basis). */
export interface InitiativeTotal {
  readonly initiativeId: string;
  readonly initiativeRef: string;
  readonly title: string;
  readonly totalDemandHours: number;
  readonly driverCount: number;
  readonly dataIncompleteDriverCount: number;
}

/** Response from `POST /api/manpower/demand`. */
export interface DemandExplorerResponse {
  readonly drivers: readonly DemandDriver[];
  readonly initiativeTotals: readonly InitiativeTotal[];
  readonly totalDemandHours: number;
  /** True when the Reconciliation_Control of R5.7 holds for this result. */
  readonly reconciliationHolds: boolean;
  /** Each clause of R5.7 separately, so a failure can name which control broke. */
  readonly reconciliation: ReconciliationClauses;
  readonly granularity: string | null;
  readonly periodIds: readonly string[];
}

const wireResponseSchema = z.object({
  drivers: z
    .array(
      z.object({
        driverId: z.string(),
        initiativeId: z.string(),
        initiativeRef: z.string(),
        title: z.string(),
        ffRole: z.string(),
        activityTypeId: z.string(),
        populationUnitId: z.string(),
        regionId: z.string().nullable().default(null),
        effortAssumptionId: z.string().nullable().default(null),
        effortAssumptionVersion: z.number().int().nullable().default(null),
        calculationDate: z.string(),
        demandHours: z.number().nullable().default(null),
        platformAllocations: z
          .array(z.object({ platformId: z.string(), hours: z.number() }))
          .default([]),
        periodAllocations: z
          .array(z.object({ periodId: z.string(), hours: z.number() }))
          .default([]),
      }),
    )
    .default([]),
  initiativeTotals: z
    .array(
      z.object({
        initiativeId: z.string(),
        initiativeRef: z.string(),
        title: z.string(),
        totalDemandHours: z.number(),
        driverCount: z.number().int().min(0),
        dataIncompleteDriverCount: z.number().int().min(0),
      }),
    )
    .default([]),
  totalDemandHours: z.number(),
  reconciliation: z
    .object({
      driverPeriodsHold: z.boolean(),
      initiativeDriversHold: z.boolean(),
      roleTotalsHold: z.boolean(),
      holds: z.boolean(),
    })
    .partial()
    .default({}),
  grid: z
    .object({
      granularity: z.string(),
      buckets: z.array(z.object({ periodId: z.string() })).default([]),
    })
    .partial()
    .default({}),
});

/** Fetches demand calculations for the Demand Explorer screen (R5.1-R5.9, R7.2). */
export async function fetchDemandExplorer(
  request: DemandExplorerRequest,
): Promise<DemandExplorerResponse> {
  // `exactOptionalPropertyTypes` and the route's `additionalProperties: false`
  // both dislike an explicit `undefined`, so optional filters are spread in only
  // when set.
  const body = await apiFetch<unknown>('/api/manpower/demand', {
    method: 'POST',
    body: JSON.stringify({
      planningYear: request.planningYear,
      granularity: request.granularity,
      ...(request.ffRole !== undefined && { ffRole: request.ffRole }),
      ...(request.regionId !== undefined && { regionId: request.regionId }),
    }),
  });

  const parsed = wireResponseSchema.parse(body);
  return {
    drivers: parsed.drivers,
    initiativeTotals: parsed.initiativeTotals,
    totalDemandHours: parsed.totalDemandHours,
    reconciliationHolds: parsed.reconciliation.holds ?? true,
    // Absent clauses default to holding: a response that omits them is an older
    // server, not a failing control, and reporting "broken" for a field that was
    // never sent would raise a false alarm on every read.
    reconciliation: {
      driverPeriodsHold: parsed.reconciliation.driverPeriodsHold ?? true,
      initiativeDriversHold: parsed.reconciliation.initiativeDriversHold ?? true,
      roleTotalsHold: parsed.reconciliation.roleTotalsHold ?? true,
      holds: parsed.reconciliation.holds ?? true,
    },
    granularity: parsed.grid.granularity ?? null,
    periodIds: (parsed.grid.buckets ?? []).map((bucket) => bucket.periodId),
  };
}

/**
 * React Query hook for the Demand Explorer calculation (R7.2).
 *
 * Keyed on every filter, so a filter change re-fetches rather than showing stale
 * data.
 */
export function useDemandExplorer(
  request: DemandExplorerRequest,
  options?: { readonly enabled?: boolean },
): UseQueryResult<DemandExplorerResponse, unknown> {
  return useQuery({
    queryKey: manpowerQueryKeys.demandExplorer({
      planningYear: request.planningYear,
      granularity: request.granularity,
      ...(request.ffRole !== undefined && { ffRole: request.ffRole }),
      ...(request.regionId !== undefined && { regionId: request.regionId }),
    }),
    queryFn: () => fetchDemandExplorer(request),
    enabled: options?.enabled ?? true,
  });
}
